import { useCallback, useEffect, useRef, useState } from 'react';

export const SIDEBAR_NARROW_STORAGE_KEY = 'backstage.sidebar.narrow.v1';

const readNarrow = () => {
  try {
    return window.localStorage.getItem(SIDEBAR_NARROW_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

const isTyping = (target: EventTarget | null) =>
  target instanceof HTMLElement &&
  (target.isContentEditable ||
    ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName));

/**
 * Narrow mode shows icons with short labels. The choice is remembered, and
 * `[` toggles it when focus isn't in a text field.
 */
export const useSidebarNarrow = (
  onKeyboardToggle?: (narrow: boolean) => void,
) => {
  const [narrow, setNarrowState] = useState(readNarrow);
  const narrowRef = useRef(narrow);
  narrowRef.current = narrow;
  const onKeyboardToggleRef = useRef(onKeyboardToggle);
  onKeyboardToggleRef.current = onKeyboardToggle;

  const setNarrow = useCallback((next: boolean) => {
    setNarrowState(next);
    try {
      window.localStorage.setItem(SIDEBAR_NARROW_STORAGE_KEY, String(next));
    } catch {
      // A disabled or full localStorage should not break navigation.
    }
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (
        event.key !== '[' ||
        event.metaKey ||
        event.ctrlKey ||
        event.altKey ||
        event.defaultPrevented ||
        isTyping(event.target)
      ) {
        return;
      }
      const next = !narrowRef.current;
      setNarrowState(next);
      try {
        window.localStorage.setItem(SIDEBAR_NARROW_STORAGE_KEY, String(next));
      } catch {
        // See setNarrow.
      }
      onKeyboardToggleRef.current?.(next);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  return { narrow, setNarrow };
};
